import { Renderer, type Token, type Tokens } from 'marked';

interface ListItemNumbered extends Tokens.ListItem {
  contractNumbering?: string;
}

const CONTRACT_CLAUSE_PATTERN = /^[\d.]*\s—\s/s;
const CONTRACT_CLAUSE_SPLIT_PATTERN = /\s—\s(.*)/s;
const SAFE_HREF_PATTERN = /^(https?:\/\/|\/|#|mailto:)/i;

const escapeHtml = (str: string) =>
  str
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const isListItemNumbered = (
  pageData: ListItemNumbered | Tokens.ListItem
): pageData is ListItemNumbered => 'contractNumbering' in pageData;

/**
 * Walks the token tree and adds a `contractNumbering` property to any
 * list item tokens that contain a clause number (e.g. "1.1 — Clause text").
 * Also removes the clause number from the token text so it doesn't get
 * rendered twice.
 *
 * Recursively walks child tokens to handle nested lists.
 *
 * Use this function as a `walkTokens` callback when calling
 * `marked(source, { walkTokens })`.
 */
export const walkTokens = (token: Token) => {
  if (token.type === 'list_item' && token.text.match(CONTRACT_CLAUSE_PATTERN)) {
    const splitString = token.text.split(CONTRACT_CLAUSE_SPLIT_PATTERN);
    const clauseMarker = splitString[0];
    const clauseText = splitString[1];

    (token as ListItemNumbered).contractNumbering = clauseMarker;

    token.text = clauseText;

    token.tokens = token.tokens?.map((childToken: Token) => {
      if (childToken.type === 'text') {
        const splitStringChild = childToken.text.split(
          CONTRACT_CLAUSE_SPLIT_PATTERN
        );
        if (splitStringChild[0] === clauseMarker) {
          childToken.text = splitStringChild[1];

          childToken.tokens = childToken.tokens?.map((grandchildToken) => {
            if (grandchildToken.type === 'text') {
              const splitStringGrandchild = grandchildToken.text.split(
                CONTRACT_CLAUSE_SPLIT_PATTERN
              );
              if (splitStringGrandchild[0] === clauseMarker) {
                grandchildToken.text = splitStringGrandchild[1];
              }
            }

            return grandchildToken;
          });
        }
      }

      return childToken;
    });
  }
};

/**
 * Shared marked renderer that sanitises both raw HTML and link hrefs.
 *
 * - `renderer.html` returns `''` so any raw HTML in markdown source
 *   (block or inline) is dropped. Markdown grammar is sufficient for
 *   FAQ / CMS content; allowlisting tag/attribute names without
 *   validating attribute *values* is too easy to bypass.
 * - `renderer.link` allowlists safe protocols (`http(s)`, `/`, `#`,
 *   `mailto:`) for hrefs, escapes href + title attributes, and adds
 *   `rel="noopener noreferrer"`. Inner content is parsed via
 *   `this.parser.parseInline(tokens)` so any embedded raw HTML in the
 *   link text goes through the no-op `renderer.html`.
 * - `renderer.listitem` checks for a `contractNumbering` property on
 *   the token (added by `walkTokens`) and, if present, adds a CSS
 *   variable to the `<li>` element for the marker content. It also adds
 *   a visually hidden `<span>` for screen readers.
 *
 * Use this renderer whenever calling `marked(source, { renderer })`.
 * Works server-side without DOM dependencies.
 */
export const customMarkdownRenderer = new Renderer();

customMarkdownRenderer.html = () => '';

customMarkdownRenderer.link = function link({
  href,
  title,
  tokens,
}: Tokens.Link): string {
  const safeHref = href && SAFE_HREF_PATTERN.test(href) ? href : '#';
  const titleAttr = title ? ` title="${escapeHtml(title)}"` : '';
  const inner = this.parser.parseInline(tokens);
  return `<a href="${escapeHtml(safeHref)}"${titleAttr} rel="noopener noreferrer">${inner}</a>`;
};

customMarkdownRenderer.listitem = function listitem(
  item: ListItemNumbered | Tokens.ListItem
): string {
  let styleClass = '';
  let contractNumberingAccessible = '';

  if (isListItemNumbered(item) && item.contractNumbering) {
    const clauseNumber = `${escapeHtml(item.contractNumbering)} —`;
    styleClass = ` style="--bullet-content: '${clauseNumber}'" class="pl-2 marker:font-mono marker:text-xs marker:content-[var(--bullet-content)]"`;
    contractNumberingAccessible = `<span class="sr-only">${clauseNumber} </span>`;
  }

  return `<li${styleClass}>${contractNumberingAccessible}${this.parser.parse(item.tokens)}</li>\n`;
};
